import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { MDText } from 'i18n-react';
import getConfig from 'next/config';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { triggerBulkBiddingDealerUploadedFile } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingDealerUploadedFile';
import { triggerBulkBiddingProcessingFailed } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingProcessingFailed';
import { logger } from 'Services/logger/logger';

import bulkBiddingTexts from './BulkBidding.texts';
import { BULK_BIDDING_CSV_FIELDS } from './bulkBiddingCSV.const';
import { validateCSVEntries } from './validateCSVEntries';
import type { ValidatedCSVEntries } from './validateCSVEntries.types';

const { publicRuntimeConfig } = getConfig();

const { BULK_BIDDING_MAX_ENQUIRIES } = publicRuntimeConfig;

const LocalT = new MDText(bulkBiddingTexts);

const triggerFailedUploadToast = () =>
	toast({
		content: LocalT.translate('toast.uploadFailed.content'),
		heading: LocalT.translate('toast.uploadFailed.heading'),
		variant: 'error',
	});

export const useBulkBiddingUpload = (onSuccess: () => void) => {
	const [validatedCSVRows, setValidatedCSVRows] = useState<Nullable<ValidatedCSVEntries>>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const { default: Papa } = await import('papaparse');
		const file = event.target.files?.[0];

		if (file) {
			triggerBulkBiddingDealerUploadedFile();

			Papa.parse(file, {
				complete: (results: Papa.ParseResult<{ [key: string]: string }>) => {
					const hasRequiredHeaders =
						BULK_BIDDING_CSV_FIELDS.BID_AMOUNT in results.data[0] &&
						BULK_BIDDING_CSV_FIELDS.VEHICLE_LINK in results.data[0];
					if (!hasRequiredHeaders) {
						triggerBulkBiddingProcessingFailed('Invalid format');
						triggerFailedUploadToast();
						return;
					}

					const nonEmptyBidRows = results.data.filter((row) => row[BULK_BIDDING_CSV_FIELDS.BID_AMOUNT]);

					if (nonEmptyBidRows.length === 0) {
						triggerBulkBiddingProcessingFailed('No bids in CSV');
						triggerFailedUploadToast();
						return;
					}

					if (nonEmptyBidRows.length > Number(BULK_BIDDING_MAX_ENQUIRIES ?? 400)) {
						triggerBulkBiddingProcessingFailed('Too many bids in CSV');
						toast({
							content: LocalT.translate('toast.uploadFailedTooManyBids.content', {
								maxEnquiries: BULK_BIDDING_MAX_ENQUIRIES,
							}),
							heading: LocalT.translate('toast.uploadFailedTooManyBids.heading'),
							variant: 'error',
						});
						return;
					}

					const validatedRows = validateCSVEntries(nonEmptyBidRows);
					setValidatedCSVRows(validatedRows);
					onSuccess();
				},
				error: (error) => {
					triggerBulkBiddingProcessingFailed('CSV parse error');
					logger.error({
						error,
						message: 'Failed to parse bulk bidding CSV',
						scope: 'BulkBidding',
					});
					triggerFailedUploadToast();
				},
				header: true,
			});
		}
	};

	const resetUpload = () => {
		if (inputRef.current) {
			inputRef.current.value = '';
		}
		setValidatedCSVRows(null);
	};

	const triggerFileInput = () => {
		if (inputRef.current) {
			resetUpload();
			inputRef.current.click();
		}
	};

	return {
		handleFileChange,
		inputRef,
		resetUpload,
		triggerFileInput,
		validatedCSVRows,
	};
};
